import { useEffect, useState } from 'react';
import axios from 'axios';

/** 
Method: useAbortableGetApi
 the method will watch on the query params and fetch the data from the endpoint
@param endpoint - The endpoint to fetch data from
@param queryParams - The query parameters to append to the endpoint
@param initialData - The initial data to set
@param transformer - The function to transform the fetched data
@param config - The configuration object
@param config.errorMessage - The error message to display if the request fails

@returns - An object containing the loading state, the fetched data, and the error message

@example
const { loading, data, error } = useAbortableGetApi<CategoryApiResponse>(
  '/api/categories',
  'ignoreCache=true',
  [],
  (data) => data,
  {
    errorMessage: 'Failed to fetch categories',
  },
);
*/
export const useAbortableGetApi = <T, K>(
  endpoint: string,
  queryParams: string,
  initialData: K,
  transformer: (data: T) => K,
  config?: {
    errorMessage?: string;
  },
) => {
  const [data, setData] = useState<K>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  useEffect(() => {
    if (!queryParams) return;

    const controller = new AbortController();
    const { signal } = controller;

    const fetchData = async () => {
      setLoading(true);
      setError(undefined);

      try {
        const response = await axios.get<T>(`${endpoint}?${queryParams}`, {
          signal,
        });

        setData(transformer(response.data));
      } catch (err) {
        if (axios.isAxiosError(err)) {
          if (err.code !== 'ERR_CANCELED')
            setError(config?.errorMessage || 'Failed to fetch data');
        } else {
          setError('An unexpected error occurred');
        }
      } finally {
        if (!signal.aborted) setLoading(false);
      }
    };

    fetchData();

    // eslint-disable-next-line consistent-return
    return () => {
      controller.abort();
    };
  }, [queryParams]);

  return {
    loading,
    data,
    error,
  };
};
